import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserInfoMember } from 'src/app/shared/models/user-info';
import { ContainerService } from 'src/app/shared/services/container/container.service';
import {
    Accessibility,
    Container,
    ContainerType,
    MembershipStatus,
} from 'src/app/shared/models/container.types';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { ToastMessageService } from '../../../shared/services/toast-message.service';
import { getContainerRouteSegment } from '../../../shared/models/container-navigation';

export interface ContainerSummaryComponentInputData {
    container: Container;
}

@Component({
    selector: 'app-container-summary',
    templateUrl: './container-summary.component.html',
    styleUrls: ['./container-summary.component.scss'],
    standalone: false,
})
export class ContainerSummaryComponent implements OnInit {
    container: Container;
    userInfo: UserInfoMember;
    imageUrl: string;
    termsAccepted: boolean = false;

    isLoading: boolean = true;
    isCloseButtonHovered: boolean = false;

    readonly containerIconPathByContainerType: Map<ContainerType, string> =
        new Map<ContainerType, string>([
            [
                ContainerType.PROJECT_MOTORIC,
                'assets/svg/sidebar-nav/motoric-icon.svg',
            ],
            [
                ContainerType.PROJECT_VISUAL,
                'assets/svg/sidebar-nav/visual-icon.svg',
            ],
            [
                ContainerType.IMAGE_DB,
                'assets/svg/sidebar-nav/picdatabase-icon.svg',
            ],
        ]);

    constructor(
        private readonly containerService: ContainerService,
        private readonly router: Router,
        private readonly toastMessageService: ToastMessageService,

        public dialogRef: MatDialogRef<
            ContainerSummaryComponent,
            ContainerSummaryComponentInputData
        >,

        @Inject(MAT_DIALOG_DATA)
        public injectedData: ContainerSummaryComponentInputData
    ) {
        this.container = this.injectedData.container;
        this.dialogRef.addPanelClass('tryb-modal-dialog');
    }

    ngOnInit(): void {
        this.determineContainerPicture();

        this.containerService
            .getOwner(this.container.id)
            .subscribe((memberInfo) => {
                this.userInfo = {
                    ...memberInfo,
                    profilePicture: memberInfo.profilePicture
                        ? 'data:image/jpeg;base64,' + memberInfo.profilePicture
                        : null,
                };

                this.isLoading = false;
            });
    }

    closeModal() {
        this.dialogRef.close();
    }

    private navigateToContainer(): void {
        this.router.navigate([
            getContainerRouteSegment(this.container.containerType),
            this.container.containerType,
            this.container.id,
        ]);
        this.closeModal();
    }

    private determineContainerPicture(): void {
        if (this.container.containerImage) {
            this.imageUrl = this.container.containerImage;
        } else {
            this.imageUrl =
                'assets/svg/reusable-svg/tryb-default-container-image.svg';
        }
    }

    private getContainerTypeKey(): 'imgdb' | 'project' {
        return this.container.containerType === ContainerType.IMAGE_DB
            ? 'imgdb'
            : 'project';
    }

    getVisitButtonTranslationKey() {
        if (this.container) {
            return `project.components.containerSummary.visitJoinButtons.visit.${this.getContainerTypeKey()}`;
        }
    }

    getJoinButtonTranslationKey() {
        if (this.container) {
            return `project.components.containerSummary.visitJoinButtons.join.${this.container.collaborationOptionsDto.accessibility}`;
        }
    }

    getJoinButtonTooltip(): string {
        return this.termsAccepted
            ? ''
            : 'project.components.containerSummary.messages.checkboxRequired';
    }

    onRequestToJoinClick() {
        if (this.termsAccepted) this.sendJoinRequest();
    }

    private sendJoinRequest() {
        this.containerService
            .createJoinRequest(this.container.id, this.termsAccepted)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    if (
                        this.container.collaborationOptionsDto.accessibility ===
                        Accessibility.OPEN
                    ) {
                        this.navigateToContainer();
                        return;
                    }
                    this.container.membershipStatus =
                        MembershipStatus.REQUESTED;

                    this.toastMessageService.showTranslatedSuccessMsg(
                        'project.components.containerSummary.messages.requestedToJoin',
                        {}
                    );
                },

                error: () => {
                    this.toastMessageService.showTranslatedErrorMsg(
                        'project.components.containerSummary.messages.requestFailed',
                        {}
                    );
                    this.termsAccepted = false;
                },
            });
    }

    onVisitContainerClick() {
        this.navigateToContainer();
    }

    get canJoinContainer(): boolean {
        return MembershipStatus.NONE === this.container?.membershipStatus;
    }

    get canVisitContainer(): boolean {
        return [MembershipStatus.MEMBER, MembershipStatus.OWNER].includes(
            this.container?.membershipStatus
        );
    }

    get isMembershipRequested() {
        return MembershipStatus.REQUESTED === this.container?.membershipStatus;
    }
}
